import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { trackPageView } from '@/lib/analytics'
import { ThemeProvider } from '@/theme/ThemeProvider'
import { AuthProvider, useAuth } from '@/auth/AuthProvider'
import { DiaryProvider } from '@/diary/DiaryProvider'
import { AppShell } from '@/components/AppShell'
import { FullScreenLoader } from '@/components/FullScreenLoader'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { SignIn } from '@/routes/SignIn'
import { Gate } from '@/routes/Gate'

// Code-split the authenticated app so the sign-in/gate load fast.
const Diary = lazy(() => import('@/routes/Diary').then((m) => ({ default: m.Diary })))
const Log = lazy(() => import('@/routes/Log').then((m) => ({ default: m.Log })))
const SavedFoods = lazy(() => import('@/routes/SavedFoods').then((m) => ({ default: m.SavedFoods })))
const Recipes = lazy(() => import('@/routes/Recipes').then((m) => ({ default: m.Recipes })))
const RecipeDetail = lazy(() => import('@/routes/RecipeDetail').then((m) => ({ default: m.RecipeDetail })))
const RecipeEditor = lazy(() => import('@/routes/RecipeEditor').then((m) => ({ default: m.RecipeEditor })))
const RecipeFavorites = lazy(() => import('@/components/recipe/FavoritesScreen').then((m) => ({ default: m.FavoritesScreen })))
const RecipeChat = lazy(() => import('@/components/recipe/RecipeChatScreen').then((m) => ({ default: m.RecipeChatScreen })))
const Progress = lazy(() => import('@/routes/Progress').then((m) => ({ default: m.Progress })))
const History = lazy(() => import('@/routes/History').then((m) => ({ default: m.History })))
const Report = lazy(() => import('@/routes/Report').then((m) => ({ default: m.Report })))
const Notes = lazy(() => import('@/routes/Notes').then((m) => ({ default: m.Notes })))
const Weight = lazy(() => import('@/routes/Weight').then((m) => ({ default: m.Weight })))
const BloodPressure = lazy(() => import('@/routes/BloodPressure').then((m) => ({ default: m.BloodPressure })))
const Settings = lazy(() => import('@/routes/Settings').then((m) => ({ default: m.Settings })))
const MealSummary = lazy(() => import('@/routes/MealSummary').then((m) => ({ default: m.MealSummary })))
const LiverStatus = lazy(() => import('@/routes/settings/LiverStatus').then((m) => ({ default: m.LiverStatus })))
const HealthConditions = lazy(() => import('@/routes/settings/HealthConditions').then((m) => ({ default: m.HealthConditions })))
const DietPreferences = lazy(() => import('@/routes/settings/DietPreferences').then((m) => ({ default: m.DietPreferences })))
const OptionalNutrients = lazy(() => import('@/routes/settings/OptionalNutrients').then((m) => ({ default: m.OptionalNutrients })))
const Fib4Calculator = lazy(() => import('@/routes/settings/Fib4Calculator').then((m) => ({ default: m.Fib4Calculator })))
const ChangePassword = lazy(() => import('@/routes/settings/ChangePassword').then((m) => ({ default: m.ChangePassword })))
const Sources = lazy(() => import('@/routes/settings/Sources').then((m) => ({ default: m.Sources })))

/** Public routes (sign-in): bounce already-authed users into the app. */
function PublicOnly() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (user) {
    // Return to the deep link the user originally requested (audit #9).
    const from = (location.state as { from?: string } | null)?.from
    return <Navigate to={from || '/diary'} replace />
  }
  return <Outlet />
}

/** Authed but not necessarily premium — the upgrade gate lives here. */
function RequireAuth() {
  const { user, loading } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  return <Outlet />
}

/** Premium/trial only — the whole app. Non-premium users go to the gate. */
function RequirePremium() {
  const { user, loading, access, profileMissing } = useAuth()
  const location = useLocation()
  if (loading) return <FullScreenLoader />
  if (!user) return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />
  // No lb_users profile → send to the gate, which shows the finish-in-app
  // screen (never the app shell, even if some entitlement exists).
  if (profileMissing) return <Navigate to="/upgrade" replace />
  if (access === 'checking') return <FullScreenLoader label="Checking your subscription…" />
  if (access === 'denied') return <Navigate to="/upgrade" replace />
  return (
    <DiaryProvider>
      <AppShell>
        <Suspense fallback={<FullScreenLoader />}>
          <Outlet />
        </Suspense>
      </AppShell>
    </DiaryProvider>
  )
}

// GA4 SPA page views — one per route change.
function PageViewTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])
  return null
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <PageViewTracker />
            <Routes>
              <Route element={<PublicOnly />}>
                <Route path="/sign-in" element={<SignIn />} />
              </Route>

              <Route element={<RequireAuth />}>
                <Route path="/upgrade" element={<Gate />} />
              </Route>

              <Route element={<RequirePremium />}>
                <Route path="/diary" element={<Diary />} />
                <Route path="/log" element={<Log />} />
                <Route path="/saved-foods" element={<SavedFoods />} />
                <Route path="/recipes" element={<Recipes />} />
                <Route path="/recipes/new" element={<RecipeEditor />} />
                <Route path="/recipes/:recipeId" element={<RecipeDetail />} />
                <Route path="/recipes/:recipeId/edit" element={<RecipeEditor />} />
                <Route path="/recipes/favorites" element={<RecipeFavorites />} />
                <Route path="/recipes/:recipeId/chat" element={<RecipeChat />} />
                <Route path="/progress" element={<Progress />} />
                <Route path="/history" element={<History />} />
                <Route path="/progress/history" element={<History />} />
                <Route path="/progress/notes" element={<Notes />} />
                <Route path="/weight" element={<Weight />} />
                <Route path="/blood-pressure" element={<BloodPressure />} />
                <Route path="/report" element={<Report />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/meal-summary/:meal" element={<MealSummary />} />
                <Route path="/settings/liver-status" element={<LiverStatus />} />
                <Route path="/settings/health-conditions" element={<HealthConditions />} />
                <Route path="/settings/diet-preferences" element={<DietPreferences />} />
                <Route path="/settings/optional-nutrients" element={<OptionalNutrients />} />
                <Route path="/settings/fib4-calculator" element={<Fib4Calculator />} />
                <Route path="/settings/change-password" element={<ChangePassword />} />
                <Route path="/settings/sources" element={<Sources />} />
              </Route>

              <Route path="/" element={<Navigate to="/diary" replace />} />
              <Route path="*" element={<Navigate to="/diary" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}
